'use strict'

/**
 * Master-lot ↔ simulated NetSuite sales-order projection and sync apply.
 *
 * One manifest-line projector feeds SO-create and inbound SO sync. Source lines
 * identify by canonical serial/SKU; optional componentKey only corroborates.
 * Catalog retains Product SKU + item internal id.
 */

import cloneDeep from 'lodash/cloneDeep'
import {
  catalogUnitWeightTroyOunces,
  manifestUnitGrossToz,
  richWeightFromTroyOunces,
} from './manifestWeights'
import {
  buildMasterLotSummarySnapshot,
} from './math'
import {
  buildCatalogManifestLine,
  withCatalogQuantity,
} from './catalogItems'
import {catalogEntriesFromManifest} from './manifestCatalog'
import {requireActiveSalesOrder} from './netsuiteSalesOrder'

// SKU-keyed source sync; optional component-key corroboration; no source item id.

/**
 * Project manifest rows into the shared SO-create/sync line contract.
 * @param {object} masterLot
 * @returns {object[]}
 */
export const projectMasterLotManifestToNetsuiteSalesOrderLines = (masterLot) => {
  const sourceUnits = masterLot?.manifest?.sourceUnits || []
  indexSourceUnitsBySku(sourceUnits)
  return [
    ...sourceUnits.map(projectSourceLine),
    ...catalogEntriesFromManifest(masterLot?.manifest).map(projectCatalogLine),
  ]
}

/**
 * Deterministic fingerprint over exact normalized projector output,
 * sorted by kind then source sku / catalog componentKey.
 * @param {object[]} lines
 * @returns {string}
 */
export const netsuiteSalesOrderLinesFingerprint = (lines) =>
  JSON.stringify(sortProjectedLines(cloneDeep(lines || [])))

export const projectMasterLotToNetsuiteSalesOrder = (masterLot, receivedAt = new Date().toISOString()) => {
  const salesOrder = requireActiveSalesOrder(masterLot)
  return {
    salesOrder: {
      netsuite_internalId: String(salesOrder.netsuite_internalId),
      tranId: salesOrder.tranId,
      status: cloneDeep(salesOrder.status),
    },
    receivedAt,
    lines: projectMasterLotManifestToNetsuiteSalesOrderLines(masterLot),
  }
}

export const applyNetsuiteSalesOrderSnapshot = (masterLot, snapshot) => {
  assertSnapshotIdentity(masterLot, snapshot)
  assertSnapshotLines(snapshot?.lines)
  const fingerprint = snapshotFingerprint(snapshot)
  if (masterLot.netsuiteSync?.salesOrder?.lastSync?.fingerprint === fingerprint) {
    return {lot: masterLot, changed: false}
  }
  const applied = applySnapshotLines(masterLot, snapshot.lines)
  const summary = summarizeAppliedManifest(applied)
  const syncEntry = buildSyncEntry(snapshot, fingerprint, applied)
  return {
    lot: assembleSyncedLot(masterLot, snapshot, applied, summary, syncEntry),
    changed: true,
  }
}

const applySnapshotLines = (masterLot, lines) => {
  const sourceUnits = cloneDeep(masterLot.manifest?.sourceUnits || [])
  const catalogEntries = cloneDeep(catalogEntriesFromManifest(masterLot.manifest))
  const sourceBySku = indexSourceUnitsBySku(sourceUnits)
  const catalogByKey = indexCatalogByComponentKey(catalogEntries)
  const incomingSourceBySku = indexInboundSourceLinesBySku(lines)
  const incomingCatalogByKey = indexInboundCatalogLinesByKey(lines)
  const nextSourceUnits = []
  const nextCatalogEntries = []
  const changes = []

  for (const [sku, line] of incomingSourceBySku) {
    const unit = sourceBySku.get(sku)
    if (!unit) throw new Error(`unknown NetSuite source SKU: ${sku}`)
    assertOptionalComponentKey(unit, line)
    nextSourceUnits.push(applySourceLine(unit, line, changes))
  }
  for (const [sku, unit] of sourceBySku) {
    if (!incomingSourceBySku.has(sku)) {
      changes.push({type: 'removed', componentKey: `source:${unit.id}`})
    }
  }

  for (const [componentKey, line] of incomingCatalogByKey) {
    if (catalogByKey.has(componentKey)) {
      nextCatalogEntries.push(applyCatalogLine(catalogByKey.get(componentKey), line, changes))
      continue
    }
    const added = catalogEntryFromLine(line)
    nextCatalogEntries.push(added)
    changes.push({type: 'added', componentKey})
  }
  for (const key of catalogByKey.keys()) {
    if (!incomingCatalogByKey.has(key)) changes.push({type: 'removed', componentKey: key})
  }

  const incoming = new Map()
  for (const [sku, line] of incomingSourceBySku) {
    incoming.set(line.componentKey || `source-sku:${sku}`, line)
  }
  for (const [componentKey, line] of incomingCatalogByKey) {
    incoming.set(componentKey, line)
  }
  return {
    incoming,
    sourceUnits: nextSourceUnits,
    catalogEntries: nextCatalogEntries,
    changes,
  }
}

const summarizeAppliedManifest = ({sourceUnits, catalogEntries}) => buildMasterLotSummarySnapshot({
  allUnits: [...sourceUnits, ...catalogEntries],
  sourceUnitCount: sourceUnits.length,
  catalogEntryCount: catalogEntries.length,
  sourceLotCount: new Set(sourceUnits.map((unit) => unit.sourceLotCode).filter(Boolean)).size,
})

const buildSyncEntry = (snapshot, fingerprint, applied) => ({
  receivedAt: snapshot.receivedAt,
  fingerprint,
  changes: applied.changes,
  lineCount: applied.incoming.size,
})

const assembleSyncedLot = (masterLot, snapshot, applied, summary, syncEntry) => {
  const priorSync = cloneDeep(masterLot.netsuiteSync?.salesOrder || {})
  const withoutReconciliation = {...priorSync}
  delete withoutReconciliation.reconciliation
  return {
    ...cloneDeep(masterLot),
    salesOrder: {
      ...cloneDeep(masterLot.salesOrder),
      status: cloneDeep(snapshot.salesOrder.status || masterLot.salesOrder.status),
    },
    manifest: {
      ...cloneDeep(masterLot.manifest),
      sourceUnits: applied.sourceUnits,
      catalogEntries: applied.catalogEntries,
    },
    summary,
    netsuiteSync: {
      ...(cloneDeep(masterLot.netsuiteSync) || {}),
      salesOrder: {
        ...withoutReconciliation,
        lastSync: syncEntry,
        history: [...(withoutReconciliation.history || []), syncEntry],
      },
    },
    updatedAt: snapshot.receivedAt,
  }
}

const projectSourceLine = (unit) => {
  const componentKey = `source:${unit.id}`
  return {
    componentKey,
    kind: 'source',
    sku: requireSku(unit.sku || unit.serialNumber, componentKey),
    quantity: manifestUnitGrossToz(unit),
    displayName: sourceDisplayName(unit),
  }
}

const projectCatalogLine = (entry) => {
  const componentKey = `catalog:${entry.id}`
  return {
    componentKey,
    kind: 'catalog',
    item: {id: requireNetsuiteItemId(entry.productNetsuiteInternalId, componentKey)},
    sku: requireSku(entry.sku, componentKey),
    quantity: Number(entry.quantity),
    unitWeightToz: catalogUnitWeightTroyOunces(entry),
    metalKey: entry.metalKey,
    purityFraction: Number(entry.purityFraction),
    locationCode: entry.locationSnapshot?.locationCode || null,
    displayName: entry.name || entry.sku,
  }
}

const applySourceLine = (unit, line, changes) => {
  if (line.kind !== 'source') throw new Error(`line kind mismatch: ${line.componentKey}`)
  const sku = requireSku(line.sku, line.componentKey)
  const quantity = positiveNumber(line.quantity, `source quantity ${line.componentKey}`)
  const currentGross = manifestUnitGrossToz(unit)
  if (quantity !== currentGross) {
    changes.push({
      type: 'quantity_changed',
      componentKey: `source:${unit.id}`,
      from: currentGross,
      to: quantity,
    })
  }
  const rest = {...unit}
  delete rest.netsuiteItemInternalId
  return {
    ...rest,
    weight: richWeightFromTroyOunces(quantity),
    sku,
    serialNumber: sku,
  }
}

const applyCatalogLine = (entry, line, changes) => {
  if (line.kind !== 'catalog') throw new Error(`line kind mismatch: ${line.componentKey}`)
  const quantity = positiveInteger(line.quantity, `catalog quantity ${line.componentKey}`)
  if (quantity !== Number(entry.quantity)) {
    changes.push({
      type: 'quantity_changed',
      componentKey: line.componentKey,
      from: Number(entry.quantity),
      to: quantity,
    })
  }
  if (
    String(line.item?.id || '') !== String(entry.productNetsuiteInternalId) ||
    line.sku !== entry.sku
  ) {
    changes.push({type: 'item_changed', componentKey: line.componentKey})
  }
  const unitWeightToz = positiveNumber(
    line.unitWeightToz || catalogUnitWeightTroyOunces(entry),
    'catalog unit weight',
  )
  return {
    ...entry,
    sku: line.sku,
    name: line.displayName || entry.name || line.sku,
    description: line.displayName || entry.description || line.sku,
    quantity,
    unitWeight: richWeightFromTroyOunces(unitWeightToz),
    weightToz: quantity * unitWeightToz,
    productNetsuiteInternalId: String(line.item?.id || entry.productNetsuiteInternalId),
  }
}

const catalogEntryFromLine = (line) => {
  const quantity = positiveInteger(line.quantity, 'added catalog quantity')
  const unitWeightToz = positiveNumber(line.unitWeightToz, 'added catalog unit weight')
  const purityFraction = Number(line.purityFraction)
  if (!(purityFraction > 0 && purityFraction <= 1)) {
    throw new Error('added catalog purity fraction must be between zero and one')
  }
  if (!line.sku || !line.item?.id || !line.metalKey || !line.locationCode) {
    throw new Error('added catalog line requires sku, NetSuite item id, metal, and location')
  }
  const baseEntry = buildCatalogManifestLine({
    sku: line.sku,
    name: line.displayName || line.sku,
    netsuite_internalId: String(line.item.id),
    metalSymbol: line.metalKey,
    weight: unitWeightToz,
    purity: purityFraction,
  }, {
    locationId: null,
    locationCode: line.locationCode,
    qtyOnHand: null,
  }, {
    id: line.componentKey.replace(/^catalog:/u, ''),
    unitWeightToz,
    weightSource: 'netsuite_sync',
    purityFraction,
    puritySource: 'netsuite_sync',
  })
  return withCatalogQuantity(baseEntry, quantity)
}

const indexSourceUnitsBySku = (sourceUnits) => {
  const map = new Map()
  for (const unit of sourceUnits) {
    const sku = requireSku(unit.sku || unit.serialNumber, `source:${unit.id}`)
    if (map.has(sku)) throw new Error(`duplicate current source SKU: ${sku}`)
    map.set(sku, unit)
  }
  return map
}

const indexCatalogByComponentKey = (catalogEntries) => {
  const map = new Map()
  for (const entry of catalogEntries) {
    map.set(`catalog:${entry.id}`, entry)
  }
  return map
}

const indexInboundSourceLinesBySku = (lines) => {
  const map = new Map()
  for (const line of lines) {
    if (line?.kind !== 'source') continue
    const sku = requireSku(line.sku, line.componentKey || 'source')
    if (map.has(sku)) throw new Error(`duplicate inbound source SKU: ${sku}`)
    map.set(sku, cloneDeep(line))
  }
  return map
}

const indexInboundCatalogLinesByKey = (lines) => {
  const map = new Map()
  for (const line of lines) {
    if (line?.kind === 'source') continue
    if (!line?.componentKey) throw new Error('NetSuite line component key required')
    if (map.has(line.componentKey)) {
      throw new Error(`duplicate NetSuite component key: ${line.componentKey}`)
    }
    if (line.kind !== 'catalog') throw new Error(`unsupported NetSuite line kind: ${line.kind}`)
    map.set(line.componentKey, cloneDeep(line))
  }
  return map
}

const assertOptionalComponentKey = (unit, line) => {
  if (!line.componentKey) return
  const expected = `source:${unit.id}`
  if (line.componentKey !== expected) {
    throw new Error(
      `source component key does not corroborate SKU ${line.sku}: expected ${expected}`,
    )
  }
}

const assertSnapshotIdentity = (masterLot, snapshot) => {
  const active = requireActiveSalesOrder(masterLot)
  if (
    String(snapshot?.salesOrder?.netsuite_internalId) !== String(active.netsuite_internalId) ||
    snapshot?.salesOrder?.tranId !== active.tranId
  ) {
    throw new Error('sales order snapshot identity does not match target lot')
  }
  if (typeof snapshot.receivedAt !== 'string' || Number.isNaN(Date.parse(snapshot.receivedAt))) {
    throw new Error('sales order snapshot receivedAt must be a valid ISO timestamp')
  }
}

const assertSnapshotLines = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) {
    throw new Error('NetSuite sales order lines required')
  }
}

const snapshotFingerprint = (snapshot) => JSON.stringify({
  salesOrder: snapshot.salesOrder,
  lines: sortProjectedLines(cloneDeep(snapshot.lines)),
})

const sortProjectedLines = (lines) => [...lines].sort((left, right) => {
  const kindCmp = String(left.kind || '').localeCompare(String(right.kind || ''))
  if (kindCmp !== 0) return kindCmp
  if (left.kind === 'source') {
    return String(left.sku || '').localeCompare(String(right.sku || ''))
  }
  return String(left.componentKey || '').localeCompare(String(right.componentKey || ''))
})

const positiveNumber = (value, label) => {
  const number = Number(value)
  if (!Number.isFinite(number) || number <= 0) throw new Error(`${label} must be positive`)
  return number
}

const positiveInteger = (value, label) => {
  const number = positiveNumber(value, label)
  if (!Number.isInteger(number)) throw new Error(`${label} must be a whole number`)
  return number
}

const sourceDisplayName = (unit) =>
  `${unit.sourceLotCode || 'RL'} · ${unit.sku || unit.serialNumber || unit.sourceBarId || unit.id}`

const requireNetsuiteItemId = (value, componentKey) => {
  if (!/^\d+$/.test(String(value || ''))) {
    throw new Error(`numeric NetSuite item id required for ${componentKey}`)
  }
  return String(value)
}

const requireSku = (value, componentKey) => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`SKU or serial required for ${componentKey}`)
  }
  return value
}
